import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useContext } from "react";
import { ThemeContext } from "../context/ThemeContext";
import {
  FaUserCircle,
  FaTachometerAlt,
  FaFileAlt,
  FaCog,
  FaSun,
  FaMoon,
  FaChevronDown,
} from "react-icons/fa";
import { FiMenu, FiX } from "react-icons/fi";

const Header = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useContext(ThemeContext);
  const location = useLocation();
  const navigate = useNavigate();

  // State management
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [deviceType, setDeviceType] = useState("desktop");

  // Refs
  const dropdownRef = useRef();

  const isHome = location.pathname === "/";

  // Enhanced responsive detection
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      if (width < 640) {
        setDeviceType("mobile");
      } else if (width < 1024) {
        setDeviceType("tablet");
      } else if (width < 1440) {
        setDeviceType("desktop");
      } else {
        setDeviceType("large");
      }

      // Close mobile menu on larger screens
      if (width >= 1024) {
        setIsMenuOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Logout handler
  const handleLogout = async () => {
    await logout();
    setDropdownOpen(false);
    setIsMenuOpen(false);
    navigate("/");
  };

  // Close dropdown when clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMenuOpen]);

  // Get responsive padding
  const getHeaderPadding = () => {
    switch (deviceType) {
      case "mobile":
        return "px-4 py-3";
      case "tablet":
        return "px-6 py-4";
      default:
        return "px-8 py-4";
    }
  };

  // Navigation items
  const navItems = [
    {
      label: "Home",
      to: !user
        ? "/"
        : user.role === "admin"
        ? "/admin/dashboard"
        : "/user/dashboard",
    },
    { label: "About", to: "/about" },
    { label: "Blogs", to: "/blogs" },
    { label: "Contact", to: "/contact" },
  ];

  const userMenuItems = user
    ? [
        {
          label: "Dashboard",
          to: user.role === "admin" ? "/admin/dashboard" : "/user/dashboard",
          icon: FaTachometerAlt,
        },
        {
          label: "My Applications",
          to: "/user/application-tracker",
          icon: FaFileAlt,
        },
        {
          label: "Settings",
          to: user.role === "admin" ? "/admin/settings" : "/user/settings",
          icon: FaCog,
        },
      ]
    : [];

  return (
    <>
      <header
        className={`
        fixed top-0 left-0 right-0 z-50
        ${getHeaderPadding()}
        bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl
        border-b border-slate-200/50 dark:border-slate-700/50
        shadow-lg shadow-slate-900/5 dark:shadow-black/20
        transition-all duration-300
      `}
      >
        <div className="flex justify-between items-center">
          {/* Logo Section */}
          <div className="flex items-center gap-3">
            <Link
              to={
                !user
                  ? "/"
                  : user.role === "admin"
                  ? "/admin/dashboard"
                  : "/user/dashboard"
              }
              className="flex items-center gap-3 group"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-200 group-hover:scale-105">
                <span className="text-white font-bold text-lg">E</span>
              </div>
              <div className="hidden sm:block">
                <h1 className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 bg-clip-text text-transparent">
                  EduConsultancy
                </h1>
                <p className="text-xs text-slate-500 dark:text-slate-400 -mt-1">
                  Your Study Abroad Partner
                </p>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="font-medium text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-slate-100/50 dark:hover:bg-slate-800/50"
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Right Section */}
          <div className="flex items-center gap-3 lg:gap-4">
            {/* Get Started Button - Desktop only, when not logged in */}
            {!user && deviceType !== "mobile" && (
              <Link
                to="/login"
                className="hidden lg:flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl font-semibold transition-all duration-200 hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl"
              >
                Get Started
              </Link>
            )}

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95"
              title={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
            >
              <div className="relative w-5 h-5">
                <FaSun
                  className={`absolute inset-0 text-yellow-500 transform transition-all duration-300 ${
                    theme === "dark"
                      ? "rotate-0 opacity-100"
                      : "rotate-90 opacity-0"
                  }`}
                />
                <FaMoon
                  className={`absolute inset-0 text-blue-600 transform transition-all duration-300 ${
                    theme === "light"
                      ? "rotate-0 opacity-100"
                      : "-rotate-90 opacity-0"
                  }`}
                />
              </div>
            </button>

            {/* User Dropdown - Desktop */}
            {user && (
              <div className="hidden lg:block relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-3 p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 group"
                >
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center font-bold text-white shadow-lg">
                    {user.name ? (
                      user.name.charAt(0).toUpperCase()
                    ) : (
                      <FaUserCircle size={20} />
                    )}
                  </div>
                  <div className="text-left min-w-0">
                    <p className="font-semibold text-slate-800 dark:text-slate-200 truncate text-sm">
                      {user.name}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                      {user.role === "admin" ? "Administrator" : "Student"}
                    </p>
                  </div>
                  <FaChevronDown
                    size={12}
                    className={`text-slate-400 transition-transform duration-200 ${
                      dropdownOpen ? "rotate-180" : "rotate-0"
                    }`}
                  />
                </button>

                {/* Desktop Dropdown Menu */}
                {dropdownOpen && (
                  <div className="absolute right-0 top-full mt-3 w-80 bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-200/50 dark:border-slate-700/50 py-4 px-2 animate-fade-in">
                    {/* Profile header */}
                    <div className="flex items-center gap-4 px-4 pb-4 border-b border-slate-200 dark:border-slate-700">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white text-xl font-bold shadow-lg">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-slate-800 dark:text-slate-200 truncate">
                          {user.name}
                        </p>
                        <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                          {user.role === "admin" ? "Administrator" : "Student"}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                          {user.email}
                        </p>
                      </div>
                    </div>

                    {/* Menu items */}
                    <div className="py-2 space-y-1">
                      {userMenuItems.map(({ to, icon: Icon, label }) => (
                        <Link
                          key={to}
                          to={to}
                          className="flex items-center gap-3 px-4 py-3 mx-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-all duration-200 group"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <Icon className="text-slate-500 dark:text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" />
                          <span className="font-medium text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-slate-100">
                            {label}
                          </span>
                        </Link>
                      ))}
                    </div>

                    {/* Logout button */}
                    <div className="pt-2 border-t border-slate-200 dark:border-slate-700 mx-2">
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl font-medium"
                      >
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Mobile User Avatar */}
            {user && deviceType !== "desktop" && deviceType !== "large" && (
              <div className="lg:hidden relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center font-bold text-white shadow-lg transition-all duration-200 hover:scale-105 active:scale-95"
                >
                  {user.name ? (
                    user.name.charAt(0).toUpperCase()
                  ) : (
                    <FaUserCircle size={20} />
                  )}
                </button>

                {/* Mobile User Dropdown */}
                {dropdownOpen && (
                  <div className="absolute right-0 top-full mt-3 w-72 bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-200/50 dark:border-slate-700/50 py-3 px-2 animate-fade-in max-h-[calc(100vh-100px)] overflow-y-auto">
                    {/* Profile header */}
                    <div className="flex items-center gap-3 px-4 pb-3 border-b border-slate-200 dark:border-slate-700">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white text-lg font-bold shadow-lg">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-slate-800 dark:text-slate-200 truncate">
                          {user.name}
                        </p>
                        <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                          {user.role === "admin" ? "Administrator" : "Student"}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                          {user.email}
                        </p>
                      </div>
                    </div>

                    {/* Menu items */}
                    <div className="py-2 space-y-1">
                      {userMenuItems.map(({ to, icon: Icon, label }) => (
                        <Link
                          key={to}
                          to={to}
                          className="flex items-center gap-3 px-4 py-3 mx-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-all duration-200 group"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <Icon
                            size={16}
                            className="text-slate-500 dark:text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors"
                          />
                          <span className="font-medium text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-slate-100">
                            {label}
                          </span>
                        </Link>
                      ))}
                    </div>

                    {/* Logout button */}
                    <div className="pt-2 border-t border-slate-200 dark:border-slate-700 mx-2">
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl font-medium"
                      >
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Mobile Hamburger Menu */}
            <button
              className="lg:hidden p-2.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 group"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle navigation menu"
            >
              <div className="relative w-5 h-5">
                <FiMenu
                  size={20}
                  className={`absolute inset-0 text-slate-700 dark:text-slate-300 transform transition-all duration-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 ${
                    isMenuOpen ? "rotate-90 opacity-0" : "rotate-0 opacity-100"
                  }`}
                />
                <FiX
                  size={20}
                  className={`absolute inset-0 text-slate-700 dark:text-slate-300 transform transition-all duration-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 ${
                    isMenuOpen ? "rotate-0 opacity-100" : "-rotate-90 opacity-0"
                  }`}
                />
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden transition-all duration-300 ease-out"
            onClick={() => setIsMenuOpen(false)}
          />

          {/* Mobile Menu */}
          <div className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl shadow-2xl z-50 lg:hidden overflow-y-auto border-l border-slate-200/50 dark:border-slate-700/50">
            {/* Mobile Menu Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200/50 dark:border-slate-700/50">
              <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200">
                Navigation
              </h2>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors duration-200"
                aria-label="Close menu"
              >
                <FiX size={20} className="text-slate-600 dark:text-slate-400" />
              </button>
            </div>

            {/* Mobile Menu Content */}
            <div className="flex flex-col py-6 px-4 space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="flex items-center px-4 py-3 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all duration-200 font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}

              {!user && (
                <Link
                  to="/login"
                  className="mx-4 mt-4 px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl font-semibold transition-all duration-200 text-center shadow-lg"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Get Started
                </Link>
              )}

              {user && (
                <div className="border-t border-slate-200/50 dark:border-slate-700/50 pt-4 mt-4 space-y-2">
                  <div className="px-4 pb-2">
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Account
                    </p>
                  </div>
                  {userMenuItems.map(({ to, icon: Icon, label }) => (
                    <Link
                      key={to}
                      to={to}
                      className="flex items-center gap-3 px-4 py-3 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all duration-200 font-medium"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Icon size={16} />
                      <span>{label}</span>
                    </Link>
                  ))}
                  <button
                    onClick={handleLogout}
                    className="w-full text-left flex items-center gap-3 px-4 py-3 mx-4 mt-4 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl transition-all duration-200 shadow-lg font-medium"
                  >
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Enhanced styles */}
      <style jsx>{`
        @keyframes fade-in {
          0% {
            opacity: 0;
            transform: translateY(-10px) scale(0.95);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        /* Enhanced focus states for accessibility */
        button:focus-visible,
        a:focus-visible {
          outline: 2px solid rgb(59 130 246);
          outline-offset: 2px;
        }

        /* Smooth transitions for all interactive elements */
        * {
          transition-property: transform, opacity, background-color,
            border-color, text-decoration-color, fill, stroke, box-shadow;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* Prevent horizontal overflow */
        html,
        body {
          overflow-x: hidden;
        }
      `}</style>
    </>
  );
};

export default Header;
